/**
 * Reparenter — temporarily adopt existing DOM nodes into a new container and
 * later restore them to their EXACT original position (M3 screen re-housing,
 * docs/menu-redesign-plan.md §3.6–3.9).
 *
 * Why: the classic tab screen's panels (3D showcase canvas, upgrade steppers,
 * bindings panel, account forms) carry live listeners and WebGL state that
 * VehicleSelect / BindingsPanel / main.ts wired once at boot, always looked up
 * by id. Moving the *same nodes* keeps every behavior without duplicating any
 * logic — and putting them back on leave keeps `?ui=classic` byte-identical.
 *
 * Mechanism: before moving a node, an inert marker is inserted where it was;
 * restore swaps the node back in front of its marker and removes the marker.
 * Markers make restore order-independent and safe even when several siblings
 * from the same parent are claimed.
 *
 * DOM surface is minimal (parentNode / insertBefore / appendChild / remove) so
 * the whole thing is unit-testable in the node vitest environment with fakes.
 */

/**
 * Minimal structural node: we only read `parentNode` to know where a node
 * lives. (Typed loosely because the DOM lib's `ParentNode` interface does not
 * declare `insertBefore` — it lives on `Node` — so a precise structural type
 * would reject real elements. Runtime parents always have it.)
 */
export interface ReparentNode {
  parentNode: object | null;
}

export interface ReparentParent {
  insertBefore(node: ReparentNode, ref: ReparentNode | null): unknown;
}

export interface ReparentTarget {
  appendChild(node: ReparentNode): unknown;
}

export interface ReparentMarker extends ReparentNode {
  remove(): void;
}

/** Browser default marker: an inert comment node. */
function defaultMakeMarker(): ReparentMarker {
  return document.createComment("reparent-anchor") as unknown as ReparentMarker;
}

interface Claim {
  node: ReparentNode;
  marker: ReparentMarker;
}

export class Reparenter {
  private claims: Claim[] = [];

  constructor(private readonly makeMarker: () => ReparentMarker = defaultMakeMarker) {}

  /** Number of currently outstanding claims. */
  size(): number {
    return this.claims.length;
  }

  /** Is this node currently claimed by this reparenter? */
  has(node: ReparentNode | null): boolean {
    return node !== null && this.claims.some((c) => c.node === node);
  }

  /**
   * Move `node` into `target`, remembering where it came from.
   * No-ops (returns false) when the node is null, parentless, or already
   * claimed — a screen re-entering must never double-claim.
   */
  claim(node: ReparentNode | null, target: ReparentTarget): boolean {
    if (!node || this.has(node)) return false;
    const parent = node.parentNode as ReparentParent | null;
    if (!parent) return false;
    const marker = this.makeMarker();
    parent.insertBefore(marker, node);
    target.appendChild(node);
    this.claims.push({ node, marker });
    return true;
  }

  /**
   * Restore every claimed node to its original position (in front of its
   * marker) and drop the markers. Reverse order for symmetry; markers make
   * the result order-independent anyway. Safe to call when empty.
   */
  releaseAll(): void {
    for (let i = this.claims.length - 1; i >= 0; i--) {
      const { node, marker } = this.claims[i]!;
      const home = marker.parentNode as ReparentParent | null;
      if (home) home.insertBefore(node, marker);
      marker.remove();
    }
    this.claims = [];
  }
}
